/* ===========================================================================
   im8-buy.js — shared coordination for the modular IM8 PDP buy experience.

   This is the SINGLE source of truth that wires together three independent,
   cross-section components that each relocate themselves into the PDP right
   column (`.im8-info-col`):

     - sections/im8-flavor.liquid   ([data-im8="flavor"])
     - sections/im8-size.liquid     ([data-im8="size"])
     - sections/im8-purchase.liquid ([data-im8="purchase"] + matrix island + CTA)

   The purchase section emits a JSON "matrix island"
   (<script type="application/json" id="im8-buy-matrix">) describing every
   one-time and subscription variant. This script reads that island, tracks the
   current flavor/size/purchase selection on a shared window.IM8 object, resolves
   the matching variant (by option-array OR title substring match), syncs the
   hidden [name="id"] inputs + price nodes, and handles add-to-cart.

   Variant titles in TruBrain look like "10 x Lemonade" — the size token ("10")
   and flavor token ("Lemonade") are matched as case-insensitive substrings.
   =========================================================================== */
(function () {
  'use strict';

  /* ---------- shared state ---------- */
  window.IM8 = window.IM8 || {
    sel: { flavor: null, size: null, purchase: 'sub' },
    matrix: null
  };
  var IM8 = window.IM8;
  if (!IM8.sel) IM8.sel = { flavor: null, size: null, purchase: 'sub' };
  if (typeof IM8.sel.purchase === 'undefined' || IM8.sel.purchase === null) {
    IM8.sel.purchase = 'sub';
  }

  /* ---------- money formatting ---------- */
  function formatMoney(cents) {
    var amount = (typeof cents === 'number') ? cents : parseInt(cents, 10);
    if (isNaN(amount)) return '';
    try {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        var fmt = (window.Shopify && window.Shopify.money_format) ? window.Shopify.money_format : '${{amount}}';
        return window.Shopify.formatMoney(amount, fmt);
      }
    } catch (e) { /* fall through */ }
    return '$' + (amount / 100).toFixed(2);
  }

  /* ---------- matrix loading ---------- */
  function loadMatrix() {
    var el = document.getElementById('im8-buy-matrix');
    if (!el) return IM8.matrix; // keep whatever we already have
    try {
      IM8.matrix = JSON.parse(el.textContent || el.innerHTML || '{}');
    } catch (e) {
      IM8.matrix = IM8.matrix || { options: {} };
    }
    return IM8.matrix;
  }

  /* ---------- variant matching (token substring) ----------
     A variant matches when BOTH the flavor token and size token appear,
     case-insensitively, either inside its `options` array OR inside its
     `title`. Tokens that are not selected are skipped. */
  function variantMatches(v, flavor, size) {
    if (!v) return false;
    var hay = '';
    if (v.options && v.options.join) hay += ' ' + v.options.join(' ');
    if (v.title) hay += ' ' + v.title;
    hay = hay.toLowerCase();

    if (flavor) {
      if (hay.indexOf(String(flavor).toLowerCase()) === -1) return false;
    }
    if (size) {
      if (hay.indexOf(String(size).toLowerCase()) === -1) return false;
    }
    return true;
  }

  function activeOption() {
    var m = IM8.matrix || {};
    var opts = m.options || {};
    return (IM8.sel.option && opts[IM8.sel.option]) ? opts[IM8.sel.option] : null;
  }
  function activePool() {
    var o = activeOption();
    return o ? (o.variants || []) : [];
  }

  function findVariant(pool, flavor, size) {
    if (!pool || !pool.length) return null;
    for (var i = 0; i < pool.length; i++) {
      if (variantMatches(pool[i], flavor, size)) return pool[i];
    }
    return null;
  }

  /* ---------- DOM sync helpers ---------- */
  function setHiddenIds(id) {
    var inputs = document.querySelectorAll('[name="id"]');
    inputs.forEach(function (inp) {
      inp.value = id != null ? String(id) : '';
      inp.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  function setPriceNodes(selector, cents) {
    var nodes = document.querySelectorAll(selector);
    if (!nodes.length) return;
    var text = (cents == null) ? '' : formatMoney(cents);
    nodes.forEach(function (n) { n.textContent = text; });
  }

  function setButtonsDisabled(disabled) {
    var btns = document.querySelectorAll('.im8-add-to-cart-btn');
    btns.forEach(function (b) {
      b.disabled = !!disabled;
      if (disabled) { b.setAttribute('disabled', 'disabled'); }
      else { b.removeAttribute('disabled'); }
    });
  }

  /* ---------- variant-scoped content (gifts / benefits / kit) ---------- */
  function applyScopes() {
    var sz = IM8.sel.size;
    var pk = IM8.sel.planKey;
    document.querySelectorAll('[data-im8-scope]').forEach(function (el) {
      var ss = el.getAttribute('data-im8-scope-size') || 'all';
      var sp = el.getAttribute('data-im8-scope-plan') || 'all';
      var sizeOk = (ss === 'all' || ss === sz);
      var planOk = (sp === 'all' || sp === pk);
      el.style.display = (sizeOk && planOk) ? '' : 'none';
    });
  }
  IM8.applyScopes = applyScopes;

  /* ---------- resolve ---------- */
  function resolve() {
    loadMatrix();
    var m = IM8.matrix || {};
    var opts = m.options || {};
    var opt = activeOption();
    var isSub = opt ? !!opt.sub : false;

    var pool = opt ? (opt.variants || []) : [];
    var v = findVariant(pool, IM8.sel.flavor, IM8.sel.size);

    var resolvedId = v ? v.id : null;
    var available = v ? !!v.available : false;

    IM8.variantId = resolvedId;
    // Selling plan comes from the active option's own product (its first plan).
    IM8.sellingPlanId = (isSub && opt && opt.plan) ? opt.plan : null;

    // Sync native cart id inputs.
    setHiddenIds(resolvedId);

    // Per-card pricing: each card shows ITS OWN product's price for the chosen
    // flavor/size; the crossed-out "was" price uses the one-time option.
    var otPool = [];
    Object.keys(opts).forEach(function (k) { if (opts[k] && !opts[k].sub) otPool = opts[k].variants || otPool; });
    var otV = findVariant(otPool, IM8.sel.flavor, IM8.sel.size);
    // Manual per-size override -> base (10-pack) -> live variant price.
    // Per-size price fields use fixed attribute suffixes -10 (first/base size) and
    // -20 (second size / override). Map the CURRENT size to a suffix by its POSITION
    // in the size selector, so any tokens work (10/20, 1/2, S/L, ...), not just "10"/"20".
    var sizeCards = document.querySelectorAll('[data-im8="size"] [data-size]');
    var sizeIdx = 0;
    for (var _i = 0; _i < sizeCards.length; _i++) {
      if (sizeCards[_i].getAttribute('data-size') === IM8.sel.size) { sizeIdx = _i; break; }
    }
    var sizeSuffix = (sizeIdx === 1) ? '20' : '10';
    function sizeVal(el, prefix) {
      if (!el) return null;
      var s = el.getAttribute('data-' + prefix + '-' + sizeSuffix);
      if ((s == null || s === '') && sizeSuffix !== '10') s = el.getAttribute('data-' + prefix + '-10');
      return (s == null || s === '') ? null : s;
    }
    document.querySelectorAll('[data-im8="purchase"] [data-option]').forEach(function (card) {
      var o = opts[card.getAttribute('data-option')];
      var cv = o ? findVariant(o.variants || [], IM8.sel.flavor, IM8.sel.size) : null;
      var nowN = card.querySelector('[data-im8-sub-price]');
      if (nowN) { var mn = sizeVal(nowN, 'now'); nowN.textContent = mn != null ? mn : (cv ? formatMoney(cv.price) : ''); }
      var wasN = card.querySelector('[data-im8-ot-price]');
      if (wasN) { var mw = sizeVal(wasN, 'was'); wasN.textContent = mw != null ? mw : (otV ? formatMoney(otV.price) : ''); }
      // Billing line: size-specific template (or base), then replace {price} with this card's live price.
      var billN = card.querySelector('[data-im8-billed]');
      if (billN) {
        var mt = sizeVal(billN, 'billed');
        var tpl = mt != null ? mt : (billN.getAttribute('data-tpl') || '');
        billN.textContent = tpl.replace(/\{\s*price\s*\}/gi, cv ? formatMoney(cv.price) : '');
      }
      var servN = card.querySelector('[data-im8-serving]');
      if (servN) { var ms = sizeVal(servN, 'serving'); if (ms != null) servN.textContent = ms; }
    });
    setPriceNodes('[data-im8-now]', v ? v.price : null);

    // Disable CTA when nothing resolved or unavailable.
    setButtonsDisabled(!v || !available);

    // Show/hide variant-scoped gifts/benefits for the current size + plan.
    applyScopes();

    document.dispatchEvent(new CustomEvent('im8:resolve', {
      detail: {
        variant: v,
        variantId: resolvedId,
        sellingPlanId: IM8.sellingPlanId,
        purchase: IM8.sel.purchase,
        option: IM8.sel.option,
        flavor: IM8.sel.flavor,
        size: IM8.sel.size,
        available: available
      }
    }));

    return v;
  }
  IM8.resolve = resolve;

  /* ---------- add to cart ---------- */
  function openCartDrawer() {
    // 1) Rebuy Smart Cart (this theme's cart when smart_cart_enabled)
    try {
      if (window.Rebuy && window.Rebuy.SmartCart) {
        var sc = window.Rebuy.SmartCart;
        if (typeof sc.refresh === 'function') { try { sc.refresh(); } catch (e) {} }
        if (typeof sc.show === 'function') { sc.show(); return true; }
        if (typeof sc.toggle === 'function') { sc.toggle(); return true; }
      }
    } catch (e) { /* fall through */ }
    // 2) Dawn native cart drawer
    var drawer = document.querySelector('cart-drawer, #CartDrawer, .cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      try { drawer.open(); return true; } catch (e) { /* fall through */ }
    }
    // 3) Dawn cart notification
    var notif = document.querySelector('cart-notification');
    if (notif && typeof notif.open === 'function') {
      try { notif.open(); return true; } catch (e) { /* fall through */ }
    }
    return false;
  }

  function addToCart() {
    var v = resolve();
    if (!v) { return; }

    var item = { id: v.id, quantity: 1 };
    if (IM8.sel.purchase === 'sub' && IM8.sellingPlanId) {
      item.selling_plan = IM8.sellingPlanId;
    }

    if (window.IM8_PREVIEW) {
      console.log('[IM8 PREVIEW] addToCart', item);
      return;
    }

    var btns = document.querySelectorAll('.im8-add-to-cart-btn');
    btns.forEach(function (b) { b.disabled = true; b.dataset.im8Label = b.textContent; b.textContent = 'Adding...'; });

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [item] })
    })
      .then(function (r) { return r.json().then(function (b) { return { ok: r.ok, body: b }; }); })
      .then(function (res) {
        btns.forEach(function (b) { b.disabled = false; if (b.dataset.im8Label) b.textContent = b.dataset.im8Label; });
        if (!res.ok) {
          alert((res.body && res.body.description) || 'Could not add to cart.');
          return;
        }
        document.dispatchEvent(new CustomEvent('im8:added', { detail: res.body }));
        if (!openCartDrawer()) {
          window.location.href = '/cart';
        }
      })
      .catch(function () {
        btns.forEach(function (b) { b.disabled = false; if (b.dataset.im8Label) b.textContent = b.dataset.im8Label; });
        alert('Could not add to cart.');
      });
  }
  IM8.addToCart = addToCart;

  /* ---------- binding ---------- */
  function readInitial(groupSel, itemSel, attr) {
    var group = document.querySelector(groupSel);
    if (!group) return null;
    var active = group.querySelector(itemSel + '.active') || group.querySelector(itemSel);
    return active ? active.getAttribute(attr) : null;
  }

  function bindFlavor() {
    var groups = document.querySelectorAll('[data-im8="flavor"]');
    if (!groups.length) return;
    var init = readInitial('[data-im8="flavor"]', '[data-flavor]', 'data-flavor');
    if (init != null && IM8.sel.flavor == null) IM8.sel.flavor = init;

    groups.forEach(function (group) {
      var items = group.querySelectorAll('[data-flavor]');
      items.forEach(function (item) {
        if (item.dataset.im8Bound) return;
        item.dataset.im8Bound = '1';
        item.addEventListener('click', function () {
          if (item.classList.contains('is-soldout')) return;
          // clear active across ALL flavor groups (cross-section safe)
          document.querySelectorAll('[data-im8="flavor"] [data-flavor]').forEach(function (c) { c.classList.remove('active'); });
          item.classList.add('active');
          IM8.sel.flavor = item.getAttribute('data-flavor');
          resolve();
        });
      });
    });
  }

  function bindSize() {
    var groups = document.querySelectorAll('[data-im8="size"]');
    if (!groups.length) return;
    var init = readInitial('[data-im8="size"]', '[data-size]', 'data-size');
    if (init != null && IM8.sel.size == null) IM8.sel.size = init;

    groups.forEach(function (group) {
      var items = group.querySelectorAll('[data-size]');
      items.forEach(function (item) {
        if (item.dataset.im8Bound) return;
        item.dataset.im8Bound = '1';
        item.addEventListener('click', function () {
          if (item.classList.contains('is-soldout')) return;
          document.querySelectorAll('[data-im8="size"] [data-size]').forEach(function (c) { c.classList.remove('active'); });
          item.classList.add('active');
          IM8.sel.size = item.getAttribute('data-size');
          resolve();
        });
      });
    });
  }

  function bindPurchase() {
    var groups = document.querySelectorAll('[data-im8="purchase"]');
    if (!groups.length) return;
    var init = readInitial('[data-im8="purchase"]', '[data-purchase]', 'data-purchase');
    if (init != null) IM8.sel.purchase = init;
    // Seed the active option (product + its plan) from the pre-selected card.
    var activeCard = document.querySelector('[data-im8="purchase"] [data-purchase].active[data-option]')
                  || document.querySelector('[data-im8="purchase"] [data-purchase].active');
    if (activeCard) { IM8.sel.option = activeCard.getAttribute('data-option'); IM8.sel.planKey = activeCard.getAttribute('data-plan-key'); }

    groups.forEach(function (group) {
      var items = group.querySelectorAll('[data-purchase]');
      items.forEach(function (item) {
        if (!item.dataset.im8Bound) {
          item.dataset.im8Bound = '1';
          item.addEventListener('click', function () {
            document.querySelectorAll('[data-im8="purchase"] [data-purchase]').forEach(function (c) { c.classList.remove('active'); });
            item.classList.add('active');
            IM8.sel.purchase = item.getAttribute('data-purchase');
            IM8.sel.option = item.getAttribute('data-option');
            IM8.sel.planKey = item.getAttribute('data-plan-key');
            resolve();
            // The one-time text link is a quick-add: selecting it adds the one-time product to cart.
            if (item.classList.contains('im8-onetime-link')) { addToCart(); }
          });
        }
      });
      // CTA button(s)
      var btns = group.querySelectorAll('.im8-add-to-cart-btn');
      btns.forEach(function (btn) {
        if (btn.dataset.im8Bound) return;
        btn.dataset.im8Bound = '1';
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          addToCart();
        });
      });
    });
  }

  function init() {
    loadMatrix();
    bindFlavor();
    bindSize();
    bindPurchase();
    resolve();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
